import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AlertTriangle, CalendarRange, Plus } from 'lucide-react';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import {
  StandardTable,
  StandardTableBody,
  StandardTableCell,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableRow,
  EmptyTableState,
  LoadingTableState,
} from '../../../components/StandardTable';
import AdminModal from '../../components/AdminModal';
import { formatDate } from '../../lib/format';

export interface LegislativeSessionRow {
  session_key: string;
  state_code: string;
  year: number;
  type: 'regular' | 'special';
  label: string;
  short_label: string;
  convene_date: string | null;
  adjourn_date: string | null;
  notes: string | null;
  is_current: boolean;
  dates_confirmed: boolean;
  counts: { bills: number; legislators: number };
}

interface SessionsPayload {
  sessions: LegislativeSessionRow[];
  currentSessionKey: string | null;
  unresolved: { bills: number; legislators: number; calendar: number };
}

const inputClasses =
  'w-full rounded-lg border border-border-light bg-white px-3 py-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary';
const labelClasses = 'mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary';

const COLUMN_COUNT = 6;

const apiError = (error: any, fallback: string) =>
  error?.response?.data?.error ?? error?.response?.data?.message ?? fallback;

const emptyForm = () => ({
  session_key: '',
  year: '',
  type: 'regular' as 'regular' | 'special',
  label: '',
  short_label: '',
  convene_date: '',
  adjourn_date: '',
  notes: '',
});

type FormState = ReturnType<typeof emptyForm>;

const formFromRow = (row: LegislativeSessionRow): FormState => ({
  session_key: row.session_key,
  year: String(row.year),
  type: row.type,
  label: row.label,
  short_label: row.short_label,
  convene_date: row.convene_date ?? '',
  adjourn_date: row.adjourn_date ?? '',
  notes: row.notes ?? '',
});

/**
 * Legislative session registry (LEGISLATIVE_SESSION_MODEL_PRD.md §5.R11, D-10).
 *
 * The one definition of what a session is and which one is current. A special
 * session is called with days of notice, so this has to be editable without a
 * deploy — the same reason election_calendars is admin-maintained.
 *
 * Convene/adjourn dates are the load-bearing fields: the resolver derives
 * "current session" from them, so editing one changes what every session-scoped
 * surface shows. A session with no dates yet falls back to year ordering, which
 * reproduces the old MAX(session_year) behaviour exactly.
 */
const SessionsView: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);

  const sessions = useQuery({
    queryKey: ['admin-legislature', 'sessions'],
    queryFn: async () => (await api.get('/admin/sessions')).data.data as SessionsPayload,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-legislature', 'sessions'] });
    // Every session picker in the app reads this; a newly registered session
    // must appear without a reload.
    queryClient.invalidateQueries({ queryKey: ['legislative-sessions'] });
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      const body = {
        session_key: form.session_key.trim().toLowerCase(),
        year: parseInt(form.year, 10),
        type: form.type,
        label: form.label.trim() || undefined,
        short_label: form.short_label.trim() || undefined,
        convene_date: form.convene_date || null,
        adjourn_date: form.adjourn_date || null,
        notes: form.notes.trim() || null,
      };
      return editingKey
        ? api.patch(`/admin/sessions/${editingKey}`, body)
        : api.post('/admin/sessions', body);
    },
    onSuccess: (response: any) => {
      toast.success(editingKey ? 'Session updated' : 'Session registered');
      // A saved session whose legislative cycle didn't follow (ELECTION_OUTCOMES_PRD §5.0).
      if (response?.data?.warning) toast.error(response.data.warning, { duration: 8000 });
      setModalOpen(false);
      setEditingKey(null);
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['admin-legislature'] });
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to save session')),
  });

  const deleteMutation = useMutation({
    mutationFn: (key: string) => api.delete(`/admin/sessions/${key}`),
    onSuccess: () => {
      toast.success('Session deleted');
      invalidate();
    },
    // The server refuses when rows reference the session and returns the counts;
    // surfacing that message is more useful than "delete failed".
    onError: (error: any) => toast.error(apiError(error, 'Failed to delete session')),
  });

  const rows = sessions.data?.sessions ?? [];
  const unresolved = sessions.data?.unresolved;
  const unresolvedTotal = unresolved
    ? unresolved.bills + unresolved.legislators + unresolved.calendar
    : 0;

  const openAdd = () => {
    setEditingKey(null);
    setForm(emptyForm());
    setModalOpen(true);
  };

  const openEdit = (row: LegislativeSessionRow) => {
    setEditingKey(row.session_key);
    setForm(formFromRow(row));
    setModalOpen(true);
  };

  const saveDisabled =
    !/^\d{4}$/.test(form.year.trim()) ||
    (!editingKey && !/^\d{4}[a-z]?$/i.test(form.session_key.trim())) ||
    (!!form.adjourn_date && !!form.convene_date && form.adjourn_date < form.convene_date);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button variant="primary" size="sm" icon={Plus} onClick={openAdd}>
          Add Session
        </Button>
      </div>

      {/*
        The importer writes session_key = NULL when a bill's session matches no
        registry row, which is how a newly called special session announces
        itself. Surfacing the count here closes the loop between the import
        noticing it and an admin registering it.
      */}
      {unresolvedTotal > 0 && (
        <div className="card flex items-center gap-3 border-l-4 border-warning-500 p-4">
          <AlertTriangle className="h-5 w-5 shrink-0 text-warning-600 dark:text-warning-400" />
          <p className="flex-1 text-sm text-text-secondary dark:text-text-dark-secondary">
            {unresolvedTotal} imported record(s) reference a session that isn't registered
            {unresolved ? ` (${unresolved.bills} bills, ${unresolved.legislators} legislators, ${unresolved.calendar} calendar events)` : ''}.
            Registering it re-resolves them on the next import.
          </p>
          <Button variant="outline" size="sm" onClick={openAdd}>Add Session</Button>
        </div>
      )}

      {sessions.isError ? (
        <div className="card flex items-center gap-3 border-l-4 border-error-500 p-4">
          <AlertTriangle className="h-5 w-5 shrink-0 text-error-600 dark:text-error-400" />
          <p className="flex-1 text-sm text-text-secondary dark:text-text-dark-secondary">
            Couldn't load legislative sessions.
          </p>
          <Button variant="outline" size="sm" onClick={() => sessions.refetch()}>Retry</Button>
        </div>
      ) : (
        <StandardTable horizontalScroll loading={sessions.isFetching && !sessions.isLoading}>
          <StandardTableHeader>
            <StandardTableRow>
              <StandardTableHeaderCell>Session</StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="md">Type</StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="lg">Convened – Adjourned</StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="xl">Bills</StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="xl">Legislators</StandardTableHeaderCell>
              <StandardTableHeaderCell>Actions</StandardTableHeaderCell>
            </StandardTableRow>
          </StandardTableHeader>
          <StandardTableBody>
            {sessions.isLoading ? (
              <LoadingTableState colSpan={COLUMN_COUNT} />
            ) : rows.length === 0 ? (
              <EmptyTableState
                colSpan={COLUMN_COUNT}
                message="No legislative sessions registered"
                icon={<CalendarRange className="h-8 w-8" />}
              />
            ) : (
              rows.map((row) => (
                <StandardTableRow key={row.session_key}>
                  <StandardTableCell>
                    <span className="font-medium text-text-primary dark:text-text-dark-primary">
                      {row.label}
                    </span>
                    {row.is_current && (
                      <span className="ml-2 rounded bg-primary-100 px-1.5 py-0.5 text-[11px] font-medium text-primary-700 dark:bg-primary-900 dark:text-primary-200">
                        Current
                      </span>
                    )}
                    {!row.dates_confirmed && (
                      <span className="ml-2 rounded bg-warning-100 px-1.5 py-0.5 text-[11px] font-medium text-warning-700 dark:bg-warning-900 dark:text-warning-200">
                        Dates needed
                      </span>
                    )}
                  </StandardTableCell>
                  <StandardTableCell responsiveHide="md">{row.type}</StandardTableCell>
                  <StandardTableCell responsiveHide="lg">
                    {row.convene_date
                      ? `${formatDate(row.convene_date)} – ${row.adjourn_date ? formatDate(row.adjourn_date) : 'in session'}`
                      : '—'}
                  </StandardTableCell>
                  <StandardTableCell responsiveHide="xl">{row.counts.bills}</StandardTableCell>
                  <StandardTableCell responsiveHide="xl">{row.counts.legislators}</StandardTableCell>
                  <StandardTableCell>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(row)}>Edit</Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/admin-next/legislature/sessions/${row.session_key}/committees`)}
                      >
                        Committees
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        // Disabled rather than hidden: the count is the reason,
                        // and it is more useful shown than silently absent.
                        disabled={row.counts.bills + row.counts.legislators > 0}
                        title={
                          row.counts.bills + row.counts.legislators > 0
                            ? 'Records reference this session'
                            : undefined
                        }
                        loading={deleteMutation.isLoading}
                        onClick={() => deleteMutation.mutate(row.session_key)}
                      >
                        Delete
                      </Button>
                    </div>
                  </StandardTableCell>
                </StandardTableRow>
              ))
            )}
          </StandardTableBody>
        </StandardTable>
      )}

      <AdminModal
        isOpen={modalOpen}
        title={editingKey ? `Edit ${form.label || editingKey}` : 'Add Legislative Session'}
        onClose={() => setModalOpen(false)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button
              variant="primary"
              size="sm"
              disabled={saveDisabled}
              loading={saveMutation.isLoading}
              onClick={() => saveMutation.mutate()}
            >
              Save Session
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClasses} htmlFor="session-key">Session key</label>
              <input
                id="session-key"
                className={inputClasses}
                value={form.session_key}
                // Immutable once anything references it — renaming would orphan
                // those rows, and the FK would reject it anyway.
                disabled={!!editingKey}
                placeholder="2027 or 2027a"
                onChange={(e) => setForm({ ...form, session_key: e.target.value })}
              />
            </div>
            <div>
              <label className={labelClasses} htmlFor="session-year">Year</label>
              <input
                id="session-year"
                className={inputClasses}
                value={form.year}
                placeholder="2027"
                onChange={(e) => setForm({ ...form, year: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className={labelClasses} htmlFor="session-type">Type</label>
            <select
              id="session-type"
              className={inputClasses}
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value as 'regular' | 'special' })}
            >
              <option value="regular">Regular</option>
              <option value="special">Special</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClasses} htmlFor="convene-date">Convene date</label>
              <input
                id="convene-date"
                type="date"
                className={inputClasses}
                value={form.convene_date}
                onChange={(e) => setForm({ ...form, convene_date: e.target.value })}
              />
            </div>
            <div>
              <label className={labelClasses} htmlFor="adjourn-date">Adjourn date</label>
              <input
                id="adjourn-date"
                type="date"
                className={inputClasses}
                value={form.adjourn_date}
                onChange={(e) => setForm({ ...form, adjourn_date: e.target.value })}
              />
            </div>
          </div>
          <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
            These dates decide which session the whole app treats as current: a session is
            current from its convene date, and stays current after adjourning until the next
            one convenes. Leave adjourn blank while it is still sitting. A session with no
            convene date is ordered by year instead.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClasses} htmlFor="session-label">Label</label>
              <input
                id="session-label"
                className={inputClasses}
                value={form.label}
                placeholder="2027 Regular Session"
                onChange={(e) => setForm({ ...form, label: e.target.value })}
              />
            </div>
            <div>
              <label className={labelClasses} htmlFor="session-short-label">Short label</label>
              <input
                id="session-short-label"
                className={inputClasses}
                value={form.short_label}
                placeholder="2027"
                onChange={(e) => setForm({ ...form, short_label: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className={labelClasses} htmlFor="session-notes">Notes</label>
            <textarea
              id="session-notes"
              className={inputClasses}
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>
        </div>
      </AdminModal>
    </div>
  );
};

export default SessionsView;
